import type { Hypothesis, JourneyContext, Task } from '@afra/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { RequestError, api } from '../../lib/api'
import { faNum } from '../platform/labels'

/**
 * «تصویر توسعه — در حال شکل‌گیری».
 *
 * Everything the agent has worked out, beside the conversation that produced
 * it: the goal, how far round the seven dimensions we have been, what it noted,
 * what it already knows, and what it asked for. The panel is the reason the
 * agent can be trusted with tools — every write it makes appears here, where
 * the director can see it and say it is wrong.
 */
/* Said to the charity, so plainly: where the guess stands, not the method's jargon. */
export const HYPOTHESIS_FA: Record<Hypothesis['status'], string> = {
  open: 'در حال بررسی',
  supported: 'شواهد بیشتری پیدا کرد',
  weakened: 'کم‌رنگ‌تر شد',
  confirmed: 'تأییدش کردید',
  refuted: 'کنار گذاشته شد',
}

export const HYPOTHESIS_BADGE: Record<Hypothesis['status'], string> = {
  open: 'badge--stored',
  supported: 'badge--warn',
  weakened: 'badge--stored',
  confirmed: 'badge--good',
  refuted: 'badge--stored',
}

export function Rail({ context, loading }: { context?: JourneyContext; loading: boolean }) {
  if (loading || !context) {
    return (
      <div className="rail">
        <div className="rail__head">
          <h3>تصویر توسعه — در حال شکل‌گیری</h3>
        </div>
        <div className="rail__block">
          <p className="muted">در حال بارگذاری…</p>
        </div>
      </div>
    )
  }

  const { goal, chapter, dimensions, noted, hypotheses, facts, tasks, progress } = context
  const openTasks = tasks.filter((t) => t.status === 'open')
  const closedTasks = tasks.filter((t) => t.status !== 'open')

  return (
    <div className="rail">
      <div className="rail__head">
        <h3>تصویر توسعه — در حال شکل‌گیری</h3>
        <p className="meta" style={{ margin: 0 }}>
          هر چیزی که افرا از حرف‌های شما برداشت کند همین‌جا دیده می‌شود.
        </p>
      </div>

      <div className="rail__block">
        <div className="rail__label">
          الان روی چه کار می‌کنیم
          {chapter.closedCount > 0 && (
            <>
              {' · '}
              <span className="num">{faNum(chapter.closedCount)}</span> بخش تمام‌شده
            </>
          )}
        </div>
        <div className="rail__value">{chapter.goalFa}</div>
      </div>

      <div className="rail__block">
        <div className="rail__label">چالشی که گفتید</div>
        <div className={goal.challengeFa ? 'rail__value' : 'rail__empty'}>
          {goal.challengeFa ?? 'هنوز مشخص نشده'}
        </div>
      </div>

      <div className="rail__block">
        <div className="rail__label">نتیجه‌ای که می‌خواهید</div>
        <div className={goal.outcomeFa ? 'rail__value' : 'rail__empty'}>
          {goal.outcomeFa ?? 'هنوز مشخص نشده'}
        </div>
      </div>

      <div className="rail__block">
        <div className="rail__label">
          آنچه تا اینجا می‌دانیم: <span className="num">{faNum(progress.known)}</span> از{' '}
          <span className="num">{faNum(progress.total)}</span>
        </div>
        <div>
          {dimensions.map((dim) => (
            <span key={dim.code} className={`dim-chip ${dim.focus ? 'is-focus' : ''}`}>
              {dim.titleFa}
              <span className="num">
                {faNum(dim.known)}/{faNum(dim.total)}
              </span>
            </span>
          ))}
        </div>
      </div>

      <div className="rail__block">
        <div className="rail__label">نکته‌هایی که یادداشت شد</div>
        {noted.length === 0 ? (
          <div className="rail__empty">هنوز نکته‌ای ثبت نشده — گفت‌وگو را ادامه بدهید</div>
        ) : (
          noted.map((item) => (
            <div key={item.id} className="note">
              <span className="note__dot" />
              <span>{item.textFa}</span>
            </div>
          ))
        )}
      </div>

      {hypotheses.length > 0 && (
        <div className="rail__block">
          <div className="rail__label">حدس‌ها دربارهٔ علت ریشه‌ای</div>
          {hypotheses.map((h) => (
            <HypothesisNote key={h.id} hypothesis={h} />
          ))}
        </div>
      )}

      <div className="rail__block">
        <div className="rail__label">آنچه از مؤسسه می‌دانیم</div>
        {facts.length === 0 ? (
          <div className="rail__empty">هنوز چیزی ثبت نشده</div>
        ) : (
          facts.map((fact) => (
            <div key={fact.code} className="note">
              <span className="note__dot" />
              <span>
                {fact.labelFa}: {fact.valueFa}
                {/* What we heard and what they confirmed are different claims,
                    and the profile page is where the second one gets made. */}
                {fact.inferred && (
                  <span className="badge badge--warn">
                    {fact.source === 'legacy_portal' ? 'از پرتال' : 'برداشت من'}
                  </span>
                )}
              </span>
            </div>
          ))
        )}
      </div>

      <div className="rail__block">
        <div className="rail__label">کارها</div>
        {tasks.length === 0 ? (
          <div className="rail__empty">هنوز کاری تعریف نشده</div>
        ) : (
          <>
            {openTasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
            {closedTasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * One guess, and the director's own say on it.
 *
 * The agent is told to ask about every guess as soon as it makes one, but the
 * director should not have to wait for that: a guess on the panel can be
 * settled from the panel. «تا حدی» is left to the conversation, where there is
 * room to say which part.
 */
function HypothesisNote({ hypothesis: h }: { hypothesis: Hypothesis }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const verdict = useMutation({
    mutationFn: (v: 'confirmed' | 'refuted') => api.hypothesisVerdict(h.id, v),
    onSuccess: (result) => {
      setError(null)
      queryClient.setQueryData(['chat-context'], result.context)
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'ثبت نشد.'),
  })
  const open = h.status === 'open' || h.status === 'supported' || h.status === 'weakened'

  return (
    <div className="note" style={h.status === 'refuted' ? { opacity: 0.6 } : undefined}>
      <span className="note__dot" />
      <span>
        {h.textFa}{' '}
        <span className={`badge ${HYPOTHESIS_BADGE[h.status]}`}>{HYPOTHESIS_FA[h.status]}</span>
        {open && (
          <span className="note__actions">
            <span className="meta">درسته؟</span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={verdict.isPending}
              onClick={() => verdict.mutate('confirmed')}
            >
              آره، درسته
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--sm"
              disabled={verdict.isPending}
              onClick={() => verdict.mutate('refuted')}
            >
              نه
            </button>
          </span>
        )}
        {error && <span className="notice notice--error">{error}</span>}
      </span>
    </div>
  )
}

export const VERDICT_FA: Record<string, string> = {
  accepted: 'انجام شد',
  needs_more: 'هنوز کامل نیست',
  rejected: 'انجام نشده',
}

/**
 * One task, and the only way to close it.
 *
 * There is no «تمام شد» button and there is not going to be one. The
 * organization writes what changed, and a judge compares that against the
 * acceptance test written when the task was made — which is why the acceptance
 * test is printed right here, before anybody starts typing.
 */
/**
 * The deadline, and where the task stands against it. Computed on the server,
 * so the panel and the agent never disagree about whether something is late.
 */
export function TaskDue({ task }: { task: Task }) {
  const left = task.daysLeft ?? 0
  const state =
    task.timing === 'overdue'
      ? { cls: 'badge--bad', fa: `${faNum(-left)} روز گذشته` }
      : task.timing === 'due_today'
        ? { cls: 'badge--warn', fa: 'مهلتش امروز است' }
        : task.timing === 'due_soon'
          ? { cls: 'badge--warn', fa: `${faNum(left)} روز مانده` }
          : task.timing === 'not_due'
            ? { cls: 'badge--stored', fa: `${faNum(left)} روز مانده` }
            : null
  return (
    <div className="meta" style={{ marginTop: 'var(--space-1)' }}>
      مهلت: {task.dueOnFa}
      {state && (
        <>
          {' '}
          <span className={`badge ${state.cls}`}>{state.fa}</span>
        </>
      )}
    </div>
  )
}

function TaskCard({ task }: { task: Task }) {
  const [open, setOpen] = useState(false)
  const [account, setAccount] = useState('')
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const claim = useMutation({
    mutationFn: (accountFa: string) => api.claimTask(task.id, accountFa),
    onSuccess: (result) => {
      setError(null)
      setOpen(false)
      setAccount('')
      queryClient.setQueryData(['chat-context'], result.context)
    },
    onError: (err) =>
      setError(err instanceof RequestError ? err.messageFa : 'ثبت نشد. دوباره امتحان کنید.'),
  })

  return (
    <div className="task">
      <div style={{ fontWeight: 700 }}>{task.actionFa}</div>
      <div className="task__done">انجام شده یعنی: {task.doneLooksLikeFa}</div>
      {task.dueOnFa && <TaskDue task={task} />}

      {task.lastVerdict && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <span
            className={`badge ${
              task.lastVerdict === 'accepted'
                ? 'badge--good'
                : task.lastVerdict === 'rejected'
                  ? 'badge--bad'
                  : 'badge--warn'
            }`}
          >
            {VERDICT_FA[task.lastVerdict]}
          </span>
          {/* Null model means nothing judged it. «نتوانستیم بررسی کنیم» must
              never be shown as «قبول نشد». */}
          {task.lastModel === null && <span className="badge badge--stored">هنوز بررسی نشده</span>}
          {task.lastReasoningFa && <div className="task__done">{task.lastReasoningFa}</div>}
        </div>
      )}

      {error && <p className="notice notice--error">{error}</p>}

      {task.status === 'open' &&
        (open ? (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <label htmlFor={`claim-${task.id}`}>چه چیزی عوض شد؟ کی انجامش داد و کِی؟</label>
            <textarea
              id={`claim-${task.id}`}
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder="مثلاً: سوم مهر توی جلسه هیئت‌مدیره تصویب شد و آقای رضایی نوشتش، الان توی گروه واتساپ هست"
            />
            <div className="row">
              <button
                type="button"
                className="btn btn--sm"
                disabled={claim.isPending || account.trim().length < 10}
                onClick={() => claim.mutate(account.trim())}
              >
                {claim.isPending ? 'در حال بررسی…' : 'بفرست برای بررسی'}
              </button>
              <button type="button" className="btn btn--quiet btn--sm" onClick={() => setOpen(false)}>
                بی‌خیال
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 'var(--space-2)' }}
            onClick={() => setOpen(true)}
          >
            انجامش دادیم
          </button>
        ))}
    </div>
  )
}
